import { Application } from '@tencent/alloy-renderer';
import { BrowserTarget } from '@tencent/alloy-renderer-browser';
import { NoteArea } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/note/note-area';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import {
    noteBodyStyle,
    placeholderBodyStyle,
} from './note-data';

declare global {
    interface Window {
        noteApp: any;
    }
}
interface INoteProps {
    canvasElement: HTMLCanvasElement,
    containerElement: HTMLDivElement,
    editMode: boolean,
    isScrollMode?: boolean,
    onExitEditing?: () => void,
    onEnterEditing?: () => void,
    onDoubleTap?: () => void,
    handleCurrentSlideChangeStart?: () => void;
    handleCurrentSlideChangeEnd?: () => void;
    resetScroll?: () => void;
    getTextAreaElemHeight?: () => number;
    handleNoteHeightUpdated?: (textRectHeight: number, applicationHeight: number) => void,
}

interface IProps extends INoteProps {
    width: number,
    height: number,
}

export class NoteLayerService {
    private readonly application: Application<any>;
    private noteArea: NoteArea;
    private editMode: boolean;
    private containerWidth: number;
    private containerHeight: number;
    private props: IProps;

    public constructor(props: IProps) {
        const { canvasElement, width, height, editMode } = props;
        this.props = props;
        this.editMode = editMode;
        this.application = new Application(new BrowserTarget(), {
            interactive: true,
            native: canvasElement,
            delayDrawHit: false,
        });
        this.application.width = width;
        this.application.height = height;
        this.containerWidth = width;
        this.containerHeight = height;
        this.application.resolution = Math.floor(window.devicePixelRatio);
        if (__IS_DEV__ || ENV.IS_TEST) {  // 测试环境挂载到window
            window.noteApp = this.application;
        }
        this.buildLayer();
    }

    public updateWithData(width: number, height: number, editMode: boolean, noteAreaShow?: boolean): void {
        // 外部容器传递的宽高为dom元素的像素值，在浏览器缩放时，会有小数点问题，需要floor
        width = Math.floor(width);
        height = Math.floor(height);
        if (!this.noteArea) return;
        if (this.shouldUpdate(width, height, editMode, noteAreaShow)) {
            this.noteArea.updateApplicationSize(width, height);
            this.resetScrollIfEditModeChange(this.noteArea.editMode, editMode);
            const delayRender = this.noteArea.editMode;
            this.noteArea.containerHeight = height;
            this.noteArea.editMode = editMode;
            this.noteArea.updateEditorPropsFromSizeChange(delayRender);
        } else if (this.noteArea.containerHeight !== height) {
            // 容器尺寸变化时，总要更新containerHeight
            this.noteArea.containerHeight = height;
        }
        this.containerWidth = width;
        this.containerHeight = height;
    }

    public dispose(): void {
        this.noteArea.dispose();
        this.application.stage.removeChild(this.noteArea);
    }

    public isShowPlaceHolder = (): boolean => this.noteArea?.isShowPlaceHolder();

    public setFocus = (): void => this.noteArea?.setFocus();

    public getTextAreaHeightSync = (): number => this.noteArea?.getTextAreaHeightSync();

    public updateTextEditorProps = (): void => this.noteArea?.updateTextEditorProps();

    public updateCacheSelectionRect = (): void => this.noteArea?.updateCacheSelectionRect();

    public scrollToView = (): void => this.noteArea?.scrollToView();

    public renderNoteFromKayBoardChange = (): void => this.noteArea?.renderNoteFromKayBoardChange();

    public setOffsetTop = (offsetTop: number): void => {
        if (this.noteArea) {
            this.noteArea.offsetTop = offsetTop;
        }
    };

    public getOffsetTop = (): number => this.noteArea?.offsetTop || 0;

    private buildLayer() {
        this.noteArea = new NoteArea({
            application: this.application,
            canvasElement: this.props.canvasElement,
            containerElement: this.props.containerElement,
            noteBodyStyle,
            placeholderBodyStyle,
            editMode: this.editMode,
            onEnterEditing: this.props.onEnterEditing,
            onExitEditing: this.props.onExitEditing,
            onDoubleTap: this.props.onDoubleTap,
            handleCurrentSlideChangeStart: this.props.handleCurrentSlideChangeStart,
            handleCurrentSlideChangeEnd: this.props.handleCurrentSlideChangeEnd,
            handleNoteHeightUpdated: this.props.handleNoteHeightUpdated,
            getTextAreaElemHeight: this.props.getTextAreaElemHeight,
            isScrollMode: !!this.props.isScrollMode,
        });
        this.application.stage.addChild(this.noteArea);
        this.application.requestRender();
    }

    private shouldUpdate = (width, height, editMode, noteAreaShow) => {
        if (noteAreaShow && this.noteArea.slideIndexHasChange()) {
            // 关闭备注时，备注停止更新。所以切换幻灯片之后开启备注时，即使尺寸无变化也需要更新
            return true;
        }
        if (this.containerWidth !== width) {
            return true;
        }
        if (editMode !== this.noteArea.editMode) {
            return true;
        }
        const appHeight = this.application.height;
        if (height === this.containerHeight) {
            return false;
        }
        if (appHeight > Math.max(height, this.containerHeight)) {
            return false;
        }
        return true;
    };

    private resetScrollIfEditModeChange = (lastEditMode, editMode) => {
        if (lastEditMode !== editMode) {
            this.props.resetScroll?.();
        }
    };

    public get isLargeNote(): boolean {
        return this.noteArea?.isLargeNote;
    }
}
